import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  PiArrowUUpLeft,
  PiCheck,
  PiDotsSixVertical,
  PiLock,
  PiPaperPlaneTilt,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import {
  TCustomerSelections,
  TOffer,
  useOfferServiceCogs,
} from '@/models/offers';
import { money } from './splitDerive';
import {
  MockScheduleStatus,
  useMockSplitSchedule,
} from '../mock/mockSplitSchedule';

const POOL_ID = 'pool';

type PurchasedService = {
  key: string;
  serviceId: number;
  name: string;
  origin: string; // "Standalone" or the CP name
  saleValue: number;
  currency: string;
};

// PROTOTYPE mock FX (EUR-relative) so cross-currency sums can be normalized to
// the paying currency. Not real rates — demo only.
const MOCK_RATES: Record<string, number> = { EUR: 1, USD: 0.92, GBP: 1.17 };
const normalize = (amount: number, from: string, to: string) =>
  (amount * (MOCK_RATES[from] ?? 1)) / (MOCK_RATES[to] ?? 1);

const STATUS_META: Record<
  MockScheduleStatus,
  { color: string; label: string }
> = {
  draft: { color: 'gray', label: 'Draft' },
  'under-review': { color: 'blue', label: 'Under review' },
  approved: { color: 'teal', label: 'Approved' },
};

const payingCurrencyOf = (offer: TOffer, services: PurchasedService[]) =>
  offer.customerSelections?.paymentCurrency ||
  offer.customerSelections?.invoiceCurrency ||
  services[0]?.currency ||
  'EUR';

// Every customer-selected service (from any CP block + standalone), tagged with
// its origin. Keyed by CP+serviceId / standalone+serviceId — NOT offer_item id,
// because proposal services share a single proposal offer_item.
function purchasedServices(
  sel: TCustomerSelections | null,
): PurchasedService[] {
  if (!sel) return [];
  const out: PurchasedService[] = [];
  const push = (
    key: string,
    origin: string,
    s: {
      serviceId: number;
      serviceName: string;
      priceAmount: number;
      priceCurrency: string;
    },
  ) =>
    out.push({
      key,
      serviceId: s.serviceId,
      name: s.serviceName,
      origin,
      saleValue: Number(s.priceAmount) || 0,
      currency: s.priceCurrency || sel.invoiceCurrency || 'EUR',
    });

  for (const p of sel.proposals || []) {
    for (const b of p.blocks || []) {
      for (const s of b.services || []) {
        if (s.selected) push(`p${p.cpId}-${s.serviceId}`, p.cpName, s);
      }
    }
  }
  for (const s of sel.standaloneServices || []) {
    if (s.selected) push(`s-${s.serviceId}`, 'Standalone', s);
  }
  return out;
}

export default function MockSplitSchedule({ offer }: { offer: TOffer }) {
  const offerId = offer.id;
  const services = useMemo(
    () => purchasedServices(offer.customerSelections),
    [offer.customerSelections],
  );
  const payCurrency = payingCurrencyOf(offer, services);

  const cogsInput = useMemo(
    () =>
      services.map((s) => ({
        serviceId: s.serviceId,
        priceCurrency: s.currency,
        priceAmount: s.saleValue,
      })),
    [services],
  );
  const { data: cogs } = useOfferServiceCogs(cogsInput);
  const cogsByService = useMemo(
    () => new Map((cogs || []).map((c) => [c.serviceId, c.cogsAmount])),
    [cogs],
  );

  const schedule = useMockSplitSchedule((s) => s.schedules[offerId]);
  const seed = useMockSplitSchedule((s) => s.seed);
  const addInstallment = useMockSplitSchedule((s) => s.addInstallment);
  const removeInstallment = useMockSplitSchedule((s) => s.removeInstallment);
  const assign = useMockSplitSchedule((s) => s.assign);
  const unassign = useMockSplitSchedule((s) => s.unassign);
  const setDueDate = useMockSplitSchedule((s) => s.setDueDate);
  const setDueAmount = useMockSplitSchedule((s) => s.setDueAmount);
  const reset = useMockSplitSchedule((s) => s.reset);
  const submit = useMockSplitSchedule((s) => s.submit);
  const approve = useMockSplitSchedule((s) => s.approve);
  const returnToDraft = useMockSplitSchedule((s) => s.returnToDraft);

  useEffect(() => {
    if (services.length) seed(offerId);
  }, [offerId, seed, services.length]);

  if (services.length === 0) {
    return (
      <Text fz="sm" c="dimmed">
        No purchased services on this offer to distribute.
      </Text>
    );
  }

  if (!schedule) return null;

  const { installments, assignment } = schedule;
  const status: MockScheduleStatus = schedule.status ?? 'draft';
  const editable = status === 'draft';

  const unassigned = services.filter((s) => !assignment[s.key]);
  const distributed = services.length - unassigned.length;
  const allDistributed = unassigned.length === 0;
  const allAmountsSet = installments.every((i) => (i.dueAmount ?? 0) > 0);
  // Each installment needs a date ≥ 1 month after the previous installment's.
  const minDateFor = (idx: number): Date => {
    const prev = idx > 0 ? installments[idx - 1] : null;
    return prev?.dueDate
      ? dayjs(prev.dueDate).add(1, 'month').toDate()
      : new Date();
  };
  const datesValid = installments.every((inst, i) => {
    if (!inst.dueDate) return false;
    if (i === 0) return true;
    const prevDate = installments[i - 1].dueDate;
    if (!prevDate) return false;
    return !dayjs(inst.dueDate).isBefore(dayjs(prevDate).add(1, 'month'));
  });
  const canSubmit = allDistributed && allAmountsSet && datesValid;

  const servicesOf = (instId: string) =>
    services.filter((s) => assignment[s.key] === instId);
  const normSale = (list: PurchasedService[]) =>
    list.reduce(
      (a, s) => a + normalize(s.saleValue, s.currency, payCurrency),
      0,
    );
  const normCogs = (list: PurchasedService[]) =>
    list.reduce(
      (a, s) =>
        a +
        normalize(cogsByService.get(s.serviceId) || 0, s.currency, payCurrency),
      0,
    );

  const totalDue = normSale(services);
  const installmentsDue = installments.reduce(
    (a, i) => a + (i.dueAmount ?? 0),
    0,
  );

  const submitReason = !allDistributed
    ? 'Place every purchased service into an installment'
    : !allAmountsSet
      ? 'Specify a due amount for every installment'
      : !datesValid
        ? 'Each installment needs a due date ≥ 1 month after the previous one'
        : '';

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    if (destination.droppableId === POOL_ID) unassign(offerId, draggableId);
    else assign(offerId, draggableId, destination.droppableId);
  };

  return (
    <Stack gap="md">
      {/* Top bar */}
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Badge color={STATUS_META[status].color} variant="light" size="lg">
            {STATUS_META[status].label}
          </Badge>
          <Button
            size="xs"
            leftSection={<PiPlus size={14} />}
            disabled={!editable}
            onClick={() => addInstallment(offerId)}
          >
            Add Installment
          </Button>
          <Tooltip label="Clear this mock schedule">
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => reset(offerId)}
            >
              Reset
            </Button>
          </Tooltip>
        </Group>
        <Stack gap={2} align="flex-end">
          <Text fz="sm" c={allDistributed ? 'teal' : 'orange'}>
            Distributed services {distributed}/{services.length}
          </Text>
          <Text fz="sm" c="dimmed">
            Installments due {money(installmentsDue, payCurrency)} / Total Due{' '}
            {money(totalDue, payCurrency)}
          </Text>
        </Stack>
      </Group>

      {/* State machine actions */}
      {status === 'under-review' && (
        <Alert color="blue" icon={<PiLock size={16} />} title="Under review">
          <Text fz="sm">
            The schedule is locked while BizDev reviews it. Approve or return it
            to draft to make changes.
          </Text>
          <Group gap="xs" mt="xs">
            <Button
              size="xs"
              color="teal"
              leftSection={<PiCheck size={14} />}
              onClick={() => approve(offerId)}
            >
              Approve
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<PiArrowUUpLeft size={14} />}
              onClick={() => returnToDraft(offerId)}
            >
              Return to draft
            </Button>
          </Group>
        </Alert>
      )}
      {status === 'approved' && (
        <Alert color="teal" icon={<PiCheck size={16} />} title="Approved">
          <Group justify="space-between" align="center">
            <Text fz="sm">This schedule has been approved.</Text>
            <Button
              size="xs"
              variant="default"
              leftSection={<PiArrowUUpLeft size={14} />}
              onClick={() => returnToDraft(offerId)}
            >
              Reopen as draft
            </Button>
          </Group>
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid gap="md">
          {/* Left: installments */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {installments.map((inst, idx) => {
                const list = servicesOf(inst.id);
                return (
                  <Paper key={inst.id} withBorder p="sm">
                    <Stack gap="sm">
                      {/* Header row */}
                      <Group
                        justify="space-between"
                        align="flex-end"
                        wrap="nowrap"
                        gap="md"
                      >
                        <Group gap="xs">
                          <Text fw={500}>
                            Installment #{idx + 1} of {installments.length}
                          </Text>
                          {editable && installments.length > 1 && (
                            <Tooltip label="Remove installment (its services return to the pool)">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() =>
                                  removeInstallment(offerId, inst.id)
                                }
                              >
                                <PiTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                        <DatePickerInput
                          size="xs"
                          label="Due date"
                          placeholder="Pick date"
                          clearable
                          disabled={!editable}
                          minDate={minDateFor(idx)}
                          value={inst.dueDate}
                          onChange={(v) => setDueDate(offerId, inst.id, v)}
                          w={150}
                        />
                        <NumberInput
                          size="xs"
                          label={`Due amount (${payCurrency})`}
                          placeholder="0"
                          thousandSeparator
                          min={0}
                          disabled={!editable}
                          value={inst.dueAmount ?? ''}
                          onChange={(v) =>
                            setDueAmount(
                              offerId,
                              inst.id,
                              v === '' ? null : Number(v),
                            )
                          }
                          w={150}
                        />
                      </Group>

                      <Divider />

                      {/* Drop zone for services */}
                      <Droppable
                        droppableId={inst.id}
                        isDropDisabled={!editable}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                              minHeight: 48,
                              borderRadius: 6,
                              padding: 4,
                              background: snapshot.isDraggingOver
                                ? 'var(--mantine-color-blue-light)'
                                : undefined,
                            }}
                          >
                            <Stack gap={4}>
                              {list.length === 0 && (
                                <Text fz="xs" c="dimmed" ta="center" py="xs">
                                  Drag services here.
                                </Text>
                              )}
                              {list.map((s, i) => (
                                <ServiceDraggable
                                  key={s.key}
                                  s={s}
                                  index={i}
                                  editable={editable}
                                  trailing={
                                    editable ? (
                                      <Tooltip label="Remove from installment">
                                        <ActionIcon
                                          size="sm"
                                          variant="subtle"
                                          color="red"
                                          onClick={() =>
                                            unassign(offerId, s.key)
                                          }
                                        >
                                          <PiX size={14} />
                                        </ActionIcon>
                                      </Tooltip>
                                    ) : null
                                  }
                                />
                              ))}
                              {provided.placeholder}
                            </Stack>
                          </Box>
                        )}
                      </Droppable>

                      <Divider />

                      {/* Footer: normalized cost + COGS */}
                      <Group
                        justify="space-between"
                        align="flex-start"
                        gap="md"
                      >
                        <Text fz="xs" c="dimmed" style={{ maxWidth: 320 }}>
                          Sum of included services, normalized to the paying
                          currency ({payCurrency}).
                        </Text>
                        <Group gap="lg">
                          <Stack gap={0} align="flex-end">
                            <Text fz="xs" c="dimmed">
                              Services value
                            </Text>
                            <Text fw={600}>
                              {money(normSale(list), payCurrency)}
                            </Text>
                          </Stack>
                          <Stack gap={0} align="flex-end">
                            <Text fz="xs" c="dimmed">
                              COGS
                            </Text>
                            <Text>{money(normCogs(list), payCurrency)}</Text>
                          </Stack>
                        </Group>
                      </Group>
                    </Stack>
                  </Paper>
                );
              })}

              {/* Submit for approval */}
              {editable && (
                <Group justify="flex-end">
                  <Tooltip label={submitReason} disabled={canSubmit}>
                    <Button
                      leftSection={<PiPaperPlaneTilt size={14} />}
                      disabled={!canSubmit}
                      onClick={() => submit(offerId)}
                    >
                      Submit for approval
                    </Button>
                  </Tooltip>
                </Group>
              )}
            </Stack>
          </Grid.Col>

          {/* Right: services to place */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper withBorder p="sm">
              <Group justify="space-between" mb="xs">
                <Text fw={500} fz="sm">
                  Services to Place
                </Text>
                <Badge
                  size="sm"
                  variant="light"
                  color={allDistributed ? 'teal' : 'orange'}
                >
                  {unassigned.length}
                </Badge>
              </Group>
              <Droppable droppableId={POOL_ID} isDropDisabled={!editable}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      minHeight: 80,
                      borderRadius: 6,
                      padding: 4,
                      background: snapshot.isDraggingOver
                        ? 'var(--mantine-color-blue-light)'
                        : undefined,
                    }}
                  >
                    <Stack gap={4}>
                      {unassigned.length === 0 && (
                        <Text fz="xs" c="dimmed" py="sm" ta="center">
                          All services placed.
                        </Text>
                      )}
                      {unassigned.map((s, i) => (
                        <ServiceDraggable
                          key={s.key}
                          s={s}
                          index={i}
                          editable={editable}
                        />
                      ))}
                      {provided.placeholder}
                    </Stack>
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Grid.Col>
        </Grid>
      </DragDropContext>
    </Stack>
  );
}

function ServiceDraggable({
  s,
  index,
  editable,
  trailing,
}: {
  s: PurchasedService;
  index: number;
  editable: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <Draggable draggableId={s.key} index={index} isDragDisabled={!editable}>
      {(provided, snapshot) => {
        const card = (
          <Paper
            ref={provided.innerRef}
            {...provided.draggableProps}
            withBorder
            p={6}
            bg={
              snapshot.isDragging
                ? 'var(--mantine-color-blue-light)'
                : undefined
            }
          >
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                <Box
                  {...provided.dragHandleProps}
                  style={{
                    cursor: editable ? 'grab' : 'not-allowed',
                    touchAction: 'none',
                    display: 'flex',
                  }}
                >
                  <PiDotsSixVertical
                    size={16}
                    color="var(--mantine-color-gray-5)"
                  />
                </Box>
                <Box style={{ minWidth: 0 }}>
                  <Text fz="sm" truncate>
                    {s.name}
                  </Text>
                  <Text fz="xs" c="dimmed">
                    {s.origin} · {s.currency}
                  </Text>
                </Box>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <Text fz="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {money(s.saleValue, s.currency)}
                </Text>
                {trailing}
              </Group>
            </Group>
          </Paper>
        );
        // While dragging, portal to <body> so the Drawer's CSS transform doesn't
        // offset the fixed-positioned drag clone from the cursor.
        return snapshot.isDragging ? createPortal(card, document.body) : card;
      }}
    </Draggable>
  );
}

// Read-only detail for ONE installment — the services it covers plus payer /
// payment method / due date / normalized Value & COGS. Used as the expanded
// detail of an installment row in the Related invoices list.
export function InstallmentDetail({
  offer,
  instId,
}: {
  offer: TOffer;
  instId: string;
}) {
  const schedule = useMockSplitSchedule((s) => s.schedules[offer.id]);
  const services = useMemo(
    () => purchasedServices(offer.customerSelections),
    [offer.customerSelections],
  );
  const payCurrency = payingCurrencyOf(offer, services);

  const cogsInput = useMemo(
    () =>
      services.map((s) => ({
        serviceId: s.serviceId,
        priceCurrency: s.currency,
        priceAmount: s.saleValue,
      })),
    [services],
  );
  const { data: cogs } = useOfferServiceCogs(cogsInput);
  const cogsByService = useMemo(
    () => new Map((cogs || []).map((c) => [c.serviceId, c.cogsAmount])),
    [cogs],
  );

  if (!schedule) return null;
  const inst = schedule.installments.find((i) => i.id === instId);
  if (!inst) return null;

  const list = services.filter((s) => schedule.assignment[s.key] === instId);
  const normSale = list.reduce(
    (a, s) => a + normalize(s.saleValue, s.currency, payCurrency),
    0,
  );
  const normCogs = list.reduce(
    (a, s) =>
      a +
      normalize(cogsByService.get(s.serviceId) || 0, s.currency, payCurrency),
    0,
  );

  const detail = (label: string, value: string) => (
    <Box>
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      <Text fz="sm">{value}</Text>
    </Box>
  );

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {detail('Payer', offer.payer?.displayName ?? '—')}
        {detail(
          'Payment method',
          offer.customerSelections?.paymentMethod || '—',
        )}
        {detail(
          'Due date',
          inst.dueDate ? dayjs(inst.dueDate).format('DD.MM.YYYY') : '—',
        )}
        {detail('Due amount', money(inst.dueAmount ?? 0, payCurrency))}
      </SimpleGrid>
      <Box>
        <Text fz="xs" c="dimmed" mb={4}>
          Covered services
        </Text>
        {list.length === 0 ? (
          <Text fz="xs" c="dimmed">
            No services placed in this installment.
          </Text>
        ) : (
          <Stack gap={2}>
            {list.map((s) => (
              <Group key={s.key} justify="space-between" gap="xs" wrap="nowrap">
                <Text fz="sm" truncate>
                  {s.name}
                  <Text span fz="xs" c="dimmed">
                    {' '}
                    · {s.origin}
                  </Text>
                </Text>
                <Text fz="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {money(s.saleValue, s.currency)}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Box>
      <Group gap="lg">
        <Text fz="xs" c="dimmed">
          Services value {money(normSale, payCurrency)}
        </Text>
        <Text fz="xs" c="dimmed">
          COGS {money(normCogs, payCurrency)}
        </Text>
      </Group>
    </Stack>
  );
}
